import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { useFeedback } from '@/components/feedback/feedback-context'
import { Switch } from '@/components/forms/switch'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'

import {
  getOidcProviderConfiguration,
  rotateOidcSigningKey,
  updateOidcProviderConfiguration,
} from './api'
import { IdpConfigurationFlow } from './idp-configuration-flow'
import { isOidcIdpReadOnly } from './idp-permissions'

const providerQueryKey = ['admin-idp-provider']
export function AdminIdpSettingsPage() {
  const { t } = useTranslation('adminIdp')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const readOnly = isOidcIdpReadOnly()
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false)
  const providerQuery = useQuery({
    queryFn: getOidcProviderConfiguration,
    queryKey: providerQueryKey,
  })
  const updateMutation = useMutation({
    mutationFn: updateOidcProviderConfiguration,
    onError: () => showFeedback({ message: t('errors.save'), tone: 'error' }),
    onSuccess: (configuration) => {
      queryClient.setQueryData(providerQueryKey, configuration)
      showFeedback({
        message: configuration.enabled ? t('success.enabled') : t('success.disabled'),
        tone: 'success',
      })
    },
  })
  const rotateMutation = useMutation({
    mutationFn: rotateOidcSigningKey,
    onError: () => showFeedback({ message: t('errors.rotateKey'), tone: 'error' }),
    onSuccess: (key) => {
      setRotateConfirmOpen(false)
      showFeedback({ message: t('success.keyRotated', { kid: key.kid }), tone: 'success' })
    },
  })
  const configuration = providerQuery.data
  const copy = (value: string) => void navigator.clipboard.writeText(value)

  const endpoints = configuration
    ? ([
        ['issuer', configuration.issuer],
        ['discoveryUrl', configuration.discovery_url],
        ['oauthDiscoveryUrl', configuration.oauth_discovery_url],
        ['authorizationEndpoint', configuration.authorization_endpoint],
        ['tokenEndpoint', configuration.token_endpoint],
        ['introspectionEndpoint', configuration.introspection_endpoint],
        ['revocationEndpoint', configuration.revocation_endpoint],
        ['userinfoEndpoint', configuration.userinfo_endpoint],
        ['jwksUri', configuration.jwks_uri],
      ] as const)
    : []

  return (
    <AdminShellPage activePath="/admin/idp/provider">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          contentClassName="max-w-[1120px]"
          description={t('description')}
          title={t('title')}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-7 lg:px-[30px] lg:py-8">
          <PageContentBoundary className="max-w-[1120px]">
            {providerQuery.isError ? (
              <p className="text-sm text-[var(--color-danger)]">{t('loadFailed')}</p>
            ) : providerQuery.isLoading || !configuration ? (
              <div className="h-56 animate-pulse rounded-xl bg-[var(--color-surface-muted)]" />
            ) : (
              <div className="grid gap-7">
                <IdpConfigurationFlow active="provider" />
                <section className="flex items-center justify-between gap-8 rounded-xl border border-[var(--color-panel-border)] bg-[rgba(25,91,255,0.035)] px-5 py-5">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold">{t('provider.title')}</h2>
                      <Badge variant={configuration.enabled ? 'success' : 'neutral'}>
                        {configuration.enabled ? t('status.enabled') : t('status.disabled')}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                      {t('provider.hint')}
                    </p>
                  </div>
                  <Switch
                    aria-label={t('provider.toggle')}
                    checked={configuration.enabled}
                    disabled={readOnly || updateMutation.isPending}
                    onCheckedChange={(enabled) => updateMutation.mutate(enabled)}
                  />
                </section>

                <section className="rounded-xl border border-[var(--color-panel-border)] bg-white p-5">
                  <div>
                    <h2 className="text-base font-semibold">{t('parameters.title')}</h2>
                    <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                      {t('parameters.hint')}
                    </p>
                  </div>
                  <dl className="mt-5 divide-y divide-[var(--color-divider)] border-y border-[var(--color-divider)]">
                    {endpoints.map(([labelKey, value]) => (
                      <div
                        className="grid gap-2 py-4 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-center md:gap-5"
                        key={labelKey}
                      >
                        <dt className="text-sm font-medium">{t(`parameters.${labelKey}`)}</dt>
                        <dd className="min-w-0 break-all font-mono text-[13px] leading-5 text-[var(--color-text-muted)]">
                          {value}
                        </dd>
                        <dd>
                          <Button onClick={() => copy(value)} size="sm" variant="ghost">
                            {t('actions.copy')}
                          </Button>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>

                <section className="flex items-center justify-between gap-8 rounded-xl border border-[var(--color-panel-border)] px-5 py-5">
                  <div>
                    <h2 className="text-base font-semibold">{t('signingKey.title')}</h2>
                    <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                      {t('signingKey.hint')}
                    </p>
                  </div>
                  <Button
                    disabled={readOnly}
                    isLoading={rotateMutation.isPending}
                    onClick={() => setRotateConfirmOpen(true)}
                    variant="secondary"
                  >
                    {t('actions.rotateKey')}
                  </Button>
                </section>
              </div>
            )}
          </PageContentBoundary>
        </div>
      </div>
      <ConfirmationDialog
        cancelLabel={t('actions.cancel')}
        confirmLabel={t('actions.rotateKey')}
        description={t('signingKey.rotateConfirm')}
        isConfirming={rotateMutation.isPending}
        onCancel={() => setRotateConfirmOpen(false)}
        onConfirm={() => rotateMutation.mutate()}
        open={rotateConfirmOpen}
        title={t('signingKey.confirmTitle')}
      />
    </AdminShellPage>
  )
}
